<template>
    <wrapper-features-templates-one-column
    noWhiteBox
    :thirdLevelMenu="getMobileAppState"
    :showTopRightAreaSlot="!getMobileAppState"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :contentFullHeight="showEmptyState"
    :showSearchSlot="!showLoader && getPaymentsOperatorsList.length && !applyErrorState && !getMobileAppState"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- We are using this slot, even tho the name is not that clear, it's the position we need for the header -->
        <template #accounts-card-slot-area>
            <div class="wrapper">
                <button-tertiary
                icon="cycle"
                buttonSize="medium"
                :label="t('payments.services.reuseButton')"
                @clicked="showReuseModal = true"/>
            </div>
        </template>
        <!-- *********************************************
        *                    Search Bar                  *
        ********************************************** -->
        <template #top-search-area-slot>
            <div v-if="!getMobileAppState" class="search-area-wrapper">
                <input-search
                v-if="!showLoader && getPaymentsOperatorsList.length && !applyErrorState"
                :params="{ placeholder: t('payments.services.searchPlaceholder')}"
                :inputValue="searchValue"
                @input="searchValue = $event"
                @rightButtonClicked="searchValue = $event"/>
            </div>
        </template>
        <!-- *********************************************
        *												 *
        *                   Empty-state                  *
        *												 *
        ********************************************** -->
        <template #empty-state-slot>
            <transition name="common-enter-animation" mode="out-in" appear>
                <!-- Empty State -->
                <empty-state-generic
                v-if="showEmptyState"
                style="height: 100%;"
                illustration="emptyState/search"
                :title="t('payments.services.searchResult.emptyState.title')"
                :description="t('payments.services.searchResult.emptyState.description')"/>
            </transition>
        </template>
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <frequent-operations
            v-if="!showLoader && getPaymentsOperatorsList.length && !applyErrorState && frequentOperationsParse.length"
            class="content-wrapper prevent-auto-margins"
            :style="{'margin-bottom': '24px'}"
            :applyErrorState="frequentOperationsErrorState"
            :frequentOperationsParse="frequentOperationsParse"
            @cardClicked="cardClickHandler"/>
        </transition>
        <transition name="common-enter-animation" mode="out-in" appear>
            <!-- *********************************************
            *              Search Results Content            *
            ********************************************** -->
            <div v-if="searchValue && !getMobileAppState" class="content-wrapper prevent-auto-margins" :class="{'full-height': showEmptyState}">
                <!-- Search List -->
                <search-list
                v-if="!showLoader && !applyErrorState"
                class="search-results-container"
                :paymentsOperatorsList="getPaymentsOperatorsList"
                :searchValue="searchValue"
                @showEmptyState="showEmptyState = $event"/>
            </div>
            <!-- *********************************************
            *                 Payments Operators             *
            ********************************************** -->
            <div v-else-if="!showLoader && !applyErrorState && !getMobileAppState" class="content-wrapper prevent-auto-margins">
                <operators-list :paymentsOperatorsList="getPaymentsOperatorsList"/>
            </div>
        </transition>
        <!-- *********************************************
        *                   Mobile Search                *
        ********************************************** -->
        <div v-if="getMobileAppState" class="search-area-wrapper">
            <input-search
            v-if="!showLoader && getPaymentsOperatorsList.length && !applyErrorState"
            preventInputClick
            :params="{ placeholder: t('payments.services.searchPlaceholder')}"
            @clicked="showMobileSearch = true"/>
        </div>
        <!-- *********************************************
        *               Mobile Payments Operators        *
        ********************************************** -->
        <div v-if="!showLoader && !applyErrorState && getMobileAppState" class="content-wrapper mobile-list-wrapper">
            <button-tertiary
            icon="cycle"
            buttonSize="small"
            :label="t('payments.services.reuseButton')"
            @clicked="showReuseModal = true"/>
            <operators-list-mobile :paymentsOperatorsList="getPaymentsOperatorsList"/>
        </div>
    </wrapper-features-templates-one-column>
    <!-- Reuse Payment Modal -->
    <reuse-modal v-if="showReuseModal" @hideMe="showReuseModal = false"/>
    <!-- Search Modal for Mobile -->
    <modal-side-search
    v-if="showMobileSearch"
    clearSearchValueOnClose
    :searchPlaceholder="t('payments.services.searchPlaceholder')"
    :searchInputValue="searchValueModal"
    :applyEmptyState="showEmptyStateModal"
    :triggerClose="triggerMobileSearchClose"
    @updateSearchValue="[searchValueModal = $event, showEmptyStateModal = false]"
    @hideMe="[showMobileSearch = false, triggerMobileSearchClose = false]">
        <!-- Search List -->
        <search-list
        class="search-results-container"
        :paymentsOperatorsList="getPaymentsOperatorsList"
        :searchValue="searchValueModal"
        @showEmptyState="showEmptyStateModal = $event"/>
    </modal-side-search>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import InputSearch from '@/components/generic/inputs/Search'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import SearchList from '@/components/features/payments/services/SearchList'
import FrequentOperations from '@/components/features/payments/services/frequentOperations/FrequentOperations'
import OperatorsList from '@/components/features/payments/services/OperatorsList'
import OperatorsListMobile from '@/components/features/payments/services/OperatorsListMobile'
import ReuseModal from '@/components/features/payments/services/ReuseModal'
import ModalSideSearch from '@/components/generic/modals/side/Search'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Assets & Helpers */
import { ref, computed } from 'vue'

/* Assets and Helpers */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { paymentsFlowsRoute, parseOperatorIdForFavoriteTab } from '@/assets/scripts/mappers/payments'
import { navigateByName } from '@/router/helpers/navigation'
import { responseRouteHandler } from '@/assets/scripts/mappers/frequentOperations'
import { getCurrentRouteMenuAdditionalData } from '@/assets/scripts/parsers/menu'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {	paymentsOperatorsFetch, requestFrequentOperations, requestPaymentsDetail } = mapActions(moduleMapper.PAYMENTS_SERVICES)
const { getPaymentsOperatorsList, getFrequentOperations } = mapGetters(moduleMapper.PAYMENTS_SERVICES)
const { getMobileAppState } = mapGetters()

/* Props received:
@params { Object } Reuse Payment Info
*/
const props = defineProps({
    params: Object
})

/* Data */
const searchValue = ref('')
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const showEmptyState = ref(false)
const showReuseModal = ref(false)
const showMobileSearch = ref(false)
const searchValueModal = ref('')
const triggerMobileSearchClose = ref(false)
const showEmptyStateModal = ref(false)
const frequentOperationsErrorState = ref(false)
const operationTypes = ref(getCurrentRouteMenuAdditionalData().listOperationIds)

const frequentOperationsParse = computed(() => {
    const operations = []

    if (!getFrequentOperations.value.length) return operations

    getFrequentOperations.value.filter(frequentOperation => {
        const duplicatedOperation = operations.find(operation => operation.operation.operatorId === frequentOperation.operation.operatorId)

		if (frequentOperation.operation.type === 'PAYMENT' && frequentOperation.operation.operatorId && !duplicatedOperation) operations.push(frequentOperation)
	})

    return operations
})
/*************************************************
*                                                *
*          Payments Operators Requests           *
*                                                *
*************************************************/
const paymentsOperatorsRequests = async () => {
    applyErrorState.value = false
	showLoader.value = true

	try {
        await paymentsOperatorsFetch()
        if (props.params) {
            reusePaymentFavoriteHandler(props.params)
        } else {
            frequentOperationsRequest()
        }
	} catch (error) {
        applyErrorState.value = true
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, paymentsOperatorsRequests)
	}
}

const frequentOperationsRequest = async () => {
    if (!operationTypes.value) {
        showLoader.value = false
        return
    }

	showLoader.value = true
    frequentOperationsErrorState.value = false

    var payload = { size: 15, operationIds: [] }

    operationTypes.value.forEach(type => {
        payload.operationIds.push(type.id)
    })

	try {
        /* TODO We may need, in a not so distante future, increase the listing size tobe able to show more operators */
        await requestFrequentOperations(payload)

        showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        frequentOperationsErrorState.value = true
        errorHandler(error.response.data, frequentOperationsRequest)
	}
}

const reusePaymentFavoriteHandler = paramsInfo => {
    /* Parsed Payments Operators List */
    const parsedPaymentsOperatorsList = []

    getPaymentsOperatorsList.value.forEach(operatorGroups => {
        operatorGroups.children.forEach(operator => {
            operator.children.forEach(elem => {
                elem.parentName = operator.name
                elem.operatorGroupName = operatorGroups.name
                parsedPaymentsOperatorsList.push(elem)
            })
        })
    })

    /* Payment Details to send in the route params when we selected a favorite row */
    const getPaymentDetailFavorite = {}

    if (paramsInfo.customerOperationView) {
        paramsInfo.customerOperationView.parameters.forEach(el => {
            getPaymentDetailFavorite[el.name] = !isNaN(el.value) && (el.key !== 'amount') ? parseInt(el.value) : el.value
        })
    }

    /* Payment Info to send in the route params when we selected a tab */
    let paymentInfo

    const paymentInfoByEntityId = parsedPaymentsOperatorsList.find(el => el.data?.emisReference && parseInt(el.data?.emisReference) === getPaymentDetailFavorite.entityId)
    const paymentInfoByOperatorId = parsedPaymentsOperatorsList.find(el => el.id === getPaymentDetailFavorite.operatorId)

    if (paymentInfoByEntityId || paymentInfoByOperatorId) {
        if (paymentInfoByEntityId) paymentInfo = paymentInfoByEntityId
        else paymentInfo = paymentInfoByOperatorId
        /* Add final action to navigate to another page */
        navigateByName(paymentsFlowsRoute(paymentInfo.flow), { params: { ...paramsInfo, ...paymentInfo, ...getPaymentDetailFavorite } })
    }
}

const cardClickHandler = async paymentInfo => {
    showOverlayLoader.value = true

    try {
        const res = await requestPaymentsDetail({ paymentId: paymentInfo.operation.id })

        const operationType = paymentInfo.operation.type

        if (operationType === 'PAYMENT' && (res.data.data.paymentsOperator.flow === 'emis_special_service' || res.data.data.paymentsOperator.flow === 'emis_service' || res.data.data.paymentsOperator.flow === 'emis_voucher')) {
            emisPaymentInfoHandler(res, paymentInfo.operation.type)
        } else navigateByName(responseRouteHandler(res, paymentInfo.operation.type), { params: res.data.data })
    } catch (error) {
        errorHandler(error.response.data, () => { cardClickHandler(paymentInfo) })
    } finally {
        showOverlayLoader.value = false
    }
}

const emisPaymentInfoHandler = (response, cardType) => {
    const responseData = response.data.data

    /* Parsed Payments Operators List Names */
    const parsedPaymentsOperatorsListNames = []
    getPaymentsOperatorsList.value.forEach(operatorGroups => {
        operatorGroups.children.forEach(operator => {
            operator.children.forEach(elem => {
                elem.parentName = operator.name
                elem.operatorGroupName = operatorGroups.name
                parsedPaymentsOperatorsListNames.push(elem)
            })
        })
    })

    const operatorInfo = parsedPaymentsOperatorsListNames.find(el => el.id === responseData.paymentsOperator.id)

    navigateByName(responseRouteHandler(response, cardType), { params: { ...responseData, ...operatorInfo } })
}
/*************************************************
*                                                *
*                 Error Handlers                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

paymentsOperatorsRequests()
</script>

<style lang="scss" scoped>
.content-wrapper, .empty-state-wrapper {
	@include commonEnteringAnimation;
    width: 100%;

    &.mobile-list-wrapper {
        margin-top: 24px;
        padding-top: 24px;

        display: flex;
        align-items: center;
        flex-direction: column;
        align-items: center;

        border-top: 1px solid var(--secondary-light-2);
    }
}

.search-area-wrapper {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.wrapper {
    width: fit-content;

    &.mobile {
        width: 100%;

        display: flex;
        justify-content: center;
    }
}
</style>
